import { Node } from '@tiptap/core';
import { blockNodeSpec, dataAttribute } from './block';
import { blockViews, initialView } from './block_views';

// Inline video: an uploaded file or a pasted link. Saved as a bare block placeholder with
// data-video-* attributes (asset = the MediaAsset's opaque external_id, link, caption, autoplay, height in px 80..1200 dragged with
// the handle under the preview; unset keeps the poster's 16:9 shape);
// Newsletters::RenderBlocks renders it, and Block::Newsletter::VideoComponent re-checks the asset belongs
// to the post and the link is https. The editor chrome is dashboard/shared/tiptap/_video_editor.html.erb.
// The block shows one view at a time (block_views.js): the editor (drop, link, upload progress, caption) or
// the preview (the server's poster, or the processing/failed status), and a block with a video opens in its
// preview.
//
// An upload is handed to the background upload manager (packs/episode_upload_manager.js, the one episode
// uploads use): the file goes straight to S3 (Dashboard::Shared::PostVideosController presigns it) from the
// manager's worker, so it keeps going when the creator leaves the editor and shows in the uploads panel.
// The node stores the asset id as soon as the manager has created it, follows the manager's progress while
// the editor is open, and polls until the video is encoded and ready. Captions are not offered yet.

const MIN_HEIGHT = 80;
const MAX_HEIGHT = 1200;
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 450; // ~30 minutes
const FAILED_STATUSES = ['error', 'presigned_upload_failed'];
const DURATION_TIMEOUT_MS = 3000;
const ACTIVE_UPLOAD_STATES = ['uploading', 'paused', 'processing'];
const MAX_BYTES = 5 * 1024 * 1024 * 1024;
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.m4v', '.webm', '.mkv'];
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\s\u0000-\u001f\u007f]/;

// Mirrors Block::Newsletter::VideoComponent#link: https only.
export function isSafeVideoLink(value) {
  const url = (value || '').trim();
  if (!url || url.length > 2048 || UNSAFE_CHARACTERS.test(url)) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname !== '' && !parsed.username && !parsed.password;
  } catch (_) {
    return false;
  }
}

const linkAttribute = {
  default: '',
  parseHTML: el => el.getAttribute('data-video-link') ?? '',
  renderHTML: attrs => (isSafeVideoLink(attrs.videoLink) ? { 'data-video-link': attrs.videoLink.trim() } : {}),
};

const autoplayAttribute = {
  default: false,
  parseHTML: el => el.getAttribute('data-video-autoplay') === 'true',
  renderHTML: attrs => (attrs.videoAutoplay ? { 'data-video-autoplay': 'true' } : {}),
};

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

async function requestJson(url, { method = 'GET', body } = {}) {
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try { data = await response.json(); } catch (_) { /* empty body */ }
  if (!response.ok) throw new Error(data.error || 'Something went wrong. Try again.');
  return data;
}

// Best effort: the browser's own read of the duration, so the player can show it before encoding finishes.
function measureDuration(file) {
  return new Promise(resolve => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const done = value => { URL.revokeObjectURL(url); resolve(value); };
    const timer = setTimeout(() => done(null), DURATION_TIMEOUT_MS);
    video.preload = 'metadata';
    video.onloadedmetadata = () => { clearTimeout(timer); done(Number.isFinite(video.duration) ? video.duration : null); };
    video.onerror = () => { clearTimeout(timer); done(null); };
    video.src = url;
  });
}

export const Video = Node.create({
  name: 'video',

  ...blockNodeSpec('video', {
    attributes: {
      videoAsset: dataAttribute('videoAsset'),
      videoLink: linkAttribute,
      videoCaption: dataAttribute('videoCaption'),
      videoAutoplay: autoplayAttribute,
      videoHeight: dataAttribute('videoHeight', { default: null }),
    },
  }),

  addOptions() {
    return {
      templates: null,         // { editor_html } — injected by the tiptap controller from the server template
      capabilities: null,      // { post_id, videos_path } — injected by the tiptap controller
      fetchRenderedHtml: null, // async (attrs) => string | null — injected by the tiptap controller
      onRenderedHtmlFetched: null,
    };
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const extension = this;
      const { post_id: postId, videos_path: videosPath, post_title: postTitle, edit_url: editUrl } = extension.options.capabilities || {};
      let currentNode = node;
      let destroyed = false;
      const manager = window.SupercastUploadManager;
      let upload = null;       // { localId } of this block's upload while its file is still going up
      let pollTimer = null;
      let previewKey = null;   // what the visible preview was rendered for

      const dom = document.createElement('div');
      dom.setAttribute('data-video-node', '');
      dom.innerHTML = extension.options.templates?.editor_html
        || '<div class="my-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500">Video</div>';

      const find = selector => dom.querySelector(selector);
      // data-state holds one or more state names; the same state can appear in both the editor and the preview.
      const states = Array.from(dom.querySelectorAll('[data-state]'));
      const showState = name => states.forEach(el => el.classList.toggle('hidden', !el.dataset.state.split(' ').includes(name)));
      const preview = find('[data-preview]');
      const linkInput = find('[data-link-input]');
      const linkError = find('[data-link-error]');
      const fileInput = find('[data-file-input]');
      const replaceButton = find('[data-replace]');
      const captionInput = find('[data-field="caption"]');
      const autoplayInput = find('[data-field="autoplay"]');
      const autoplayRow = find('[data-autoplay-row]');
      const posterNote = find('[data-poster-note]');
      const resizeHandle = find('[data-resize-handle]');

      const pos = () => (typeof getPos === 'function' ? getPos() : null);

      const updateAttrs = attrs => {
        const at = pos();
        if (at === null || at === undefined || destroyed) return;
        editor.view.dispatch(editor.state.tr.setNodeMarkup(at, undefined, { ...currentNode.attrs, ...attrs }));
      };

      const showFailure = message => {
        dom.querySelectorAll('[data-failed-message]').forEach(el => { el.textContent = message; });
        showState('failed');
      };

      // A video, or a safe link, and no upload still going.
      const canFinish = () => !upload && Boolean(currentNode.attrs.videoAsset || isSafeVideoLink(currentNode.attrs.videoLink));
      const views = blockViews(dom, { canFinish });

      let pollGen = 0;
      const stopPolling = () => { clearTimeout(pollTimer); pollTimer = null; pollGen += 1; };

      const renderPreview = async () => {
        const { videoAsset, videoLink } = currentNode.attrs;
        const key = `${videoAsset}|${videoLink}`;
        if (key === previewKey || !extension.options.fetchRenderedHtml) return;
        previewKey = key;
        const html = await extension.options.fetchRenderedHtml({ postId, videoAsset, videoLink, videoHeight: currentNode.attrs.videoHeight });
        if (!destroyed && key === previewKey && preview) {
          preview.innerHTML = html || '';
          resizeHandle?.classList.toggle('hidden', !previewPoster());
          resizeHandle?.classList.toggle('flex', Boolean(previewPoster()));
        }
      };

      // The element the creator's height applies to; a pasted link's card has none.
      const previewPoster = () => preview?.querySelector('[data-video-poster]');
      const clampHeight = value => Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(value)));

      resizeHandle?.addEventListener('pointerdown', e => {
        const poster = previewPoster();
        if (!poster) return;
        e.preventDefault();
        resizeHandle.setPointerCapture?.(e.pointerId);
        const startY = e.clientY;
        const startHeight = poster.getBoundingClientRect().height;
        let height = clampHeight(startHeight);
        const move = ev => {
          height = clampHeight(startHeight + ev.clientY - startY);
          poster.style.height = `${height}px`;
          poster.style.maxHeight = `${height}px`;
        };
        const end = () => {
          resizeHandle.removeEventListener('pointermove', move);
          resizeHandle.removeEventListener('pointerup', end);
          resizeHandle.removeEventListener('pointercancel', end);
          updateAttrs({ videoHeight: height });
        };
        resizeHandle.addEventListener('pointermove', move);
        resizeHandle.addEventListener('pointerup', end);
        resizeHandle.addEventListener('pointercancel', end);
      });

      const checkAsset = async (assetId, attempt = 1, gen = pollGen) => {
        const stale = () => destroyed || upload || gen !== pollGen || currentNode.attrs.videoAsset !== assetId;
        if (stale()) return;
        try {
          const status = await requestJson(`${videosPath}/${assetId}`);
          if (stale()) return;
          if (status.ready) {
            showState('ready');
            renderPreview();
            return;
          }
          // Still waiting for its file with nothing uploading it any more (the tab was closed mid-transfer).
          const abandoned = status.status === 'presigned_upload_started' && !manager?.getPostVideoUpload(postId, currentNode.attrs.blockId);
          if (FAILED_STATUSES.includes(status.status) || abandoned) {
            showFailure(status.status === 'error'
              ? "We couldn't process that video. Try another file."
              : "That upload didn't finish. Choose the video again.");
            return;
          }
          showState('processing');
        } catch (_) { /* keep polling through a blip */ }
        if (attempt < POLL_MAX_ATTEMPTS) pollTimer = setTimeout(() => checkAsset(assetId, attempt + 1, gen), POLL_INTERVAL_MS);
      };

      // Mirrors the node's attrs into the form and picks the visible state.
      function syncFromNode() {
        const { videoAsset, videoLink, videoCaption, videoAutoplay } = currentNode.attrs;
        if (captionInput && captionInput !== document.activeElement) captionInput.value = videoCaption || '';
        if (autoplayInput) autoplayInput.checked = Boolean(videoAutoplay);

        if (upload) { showState('uploading'); views.showEditor(); return; }

        if (videoAsset) {
          autoplayRow?.classList.remove('hidden');
          posterNote?.classList.remove('hidden');
          // Processing until the status says ready (or the upload broke).
          stopPolling();
          showState('processing');
          checkAsset(videoAsset);
        } else if (videoLink) {
          autoplayRow?.classList.add('hidden');
          posterNote?.classList.add('hidden');
          showState('ready');
          renderPreview();
        } else {
          stopPolling();
          previewKey = null;
          if (preview) preview.innerHTML = '';
          showState('empty');
        }
        views.refresh();
      }

      // The video node with this blockId wherever it sits now (a drag or an undo can move or re-create it),
      // so a finished upload lands on its own node even if the view that started it is gone.
      const findBlock = blockId => {
        let found = null;
        editor.state.doc.descendants((child, position) => {
          if (!found && child.type.name === 'video' && child.attrs.blockId === blockId) found = { node: child, position };
        });
        return found;
      };

      const showProgress = record => {
        const bar = find('[data-progress]');
        const label = find('[data-upload-label]');
        const percent = record.bytesTotal ? Math.round((record.bytesUploaded / record.bytesTotal) * 100) : 0;
        if (bar) bar.style.width = `${percent}%`;
        if (label) {
          if (record.state === 'paused') label.textContent = 'Upload paused';
          else label.textContent = record.state === 'processing' ? 'Finishing up...' : 'Uploading your video...';
        }
      };

      // Follows the manager's record for this block: progress while the file goes up, the asset id once the
      // manager has created it, and the hand-off to polling (or the failure) when the transfer ends.
      const onManagerEvent = ({ type, upload: record }) => {
        if (!record || record.kind !== 'post_video' || destroyed) return;
        if (String(record.postId) !== String(postId) || record.blockId !== currentNode.attrs.blockId) return;

        if (type === 'cancel') {
          upload = null;
          const target = findBlock(record.blockId);
          if (target && target.node.attrs.videoAsset === record.externalId) updateAttrs({ videoAsset: '' });
          syncFromNode();
          return;
        }

        if (ACTIVE_UPLOAD_STATES.includes(record.state)) {
          const attaching = !upload || upload.localId !== record.localId;
          upload = { localId: record.localId };
          if (type === 'slot-ready') {
            previewKey = null;
            updateAttrs({ videoAsset: record.externalId, videoLink: '' });
          }
          if (attaching) { showState('uploading'); views.showEditor(); }
          showProgress(record);
          views.refresh();
          return;
        }

        // Transcoding, completed or failed: the file is no longer going up.
        upload = null;
        if (record.state === 'error') {
          if (currentNode.attrs.videoAsset === record.externalId) updateAttrs({ videoAsset: '' });
          showFailure(record.errorMessage || "That video didn't upload.");
          return;
        }
        previewKey = null;
        syncFromNode();
      };
      const unsubscribe = manager?.subscribe(onManagerEvent);

      const startUpload = async file => {
        const extensionOk = VIDEO_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));
        if (!extensionOk) return showFailure('Choose an MP4, MOV, M4V, WebM or MKV video.');
        if (file.size > MAX_BYTES) return showFailure('That file is too large. Videos can be up to 5 GB.');
        if (!videosPath || !manager) return showFailure('Save this email once, then add the video.');

        const bar = find('[data-progress]');
        const label = find('[data-upload-label]');
        if (bar) bar.style.width = '0%';
        if (label) label.textContent = 'Uploading your video...';

        const localId = manager.start({
          file,
          mediaType: 'video',
          kind: 'post_video',
          postId,
          blockId: currentNode.attrs.blockId,
          videosPath,
          label: `${postTitle || 'Email'} · ${file.name}`,
          href: editUrl,
        });
        upload = { localId };
        views.showEditor();
        syncFromNode();
        // The browser's own read of the duration; the manager sends it with the details once the file is up.
        measureDuration(file).then(duration => manager.updateUpload(localId, { duration }));
      };

      // An upload started before this view existed (the editor was left and reopened, or the node re-rendered).
      const resumed = manager?.getPostVideoUpload(postId, currentNode.attrs.blockId);
      if (resumed && ACTIVE_UPLOAD_STATES.includes(resumed.state)) upload = { localId: resumed.localId };

      find('[data-choose]')?.addEventListener('click', () => fileInput?.click());
      fileInput?.addEventListener('change', e => {
        const [file] = e.target.files || [];
        e.target.value = '';
        if (file) startUpload(file);
      });

      const dropzone = find('[data-dropzone]');
      dropzone?.addEventListener('dragover', e => e.preventDefault());
      dropzone?.addEventListener('drop', e => {
        e.preventDefault();
        const [file] = e.dataTransfer?.files || [];
        if (file) startUpload(file);
      });

      const addLink = () => {
        const value = (linkInput?.value || '').trim();
        if (!isSafeVideoLink(value)) { linkError?.classList.remove('hidden'); return; }
        linkError?.classList.add('hidden');
        linkInput.value = '';
        previewKey = null;
        updateAttrs({ videoLink: value, videoAsset: '' });
        syncFromNode();
      };
      find('[data-add-link]')?.addEventListener('click', addLink);
      linkInput?.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } });

      captionInput?.addEventListener('input', e => updateAttrs({ videoCaption: e.target.value }));
      autoplayInput?.addEventListener('change', e => updateAttrs({ videoAutoplay: e.target.checked }));

      const reset = () => {
        stopPolling();
        previewKey = null;
        updateAttrs({ videoAsset: '', videoLink: '', videoAutoplay: false });
        views.showEditor();
        syncFromNode();
      };
      replaceButton?.addEventListener('click', reset);
      dom.querySelectorAll('[data-retry]').forEach(button => button.addEventListener('click', reset));
      find('[data-cancel-upload]')?.addEventListener('click', () => { if (upload) manager.cancel(upload.localId); });

      find('[data-remove]')?.addEventListener('mousedown', e => {
        e.preventDefault();
        const at = pos();
        if (at === null || at === undefined) return;
        if (upload) manager.cancel(upload.localId);
        const current = editor.state.doc.nodeAt(at);
        if (current) editor.chain().focus().deleteRange({ from: at, to: at + current.nodeSize }).run();
      });

      // The preview is made of real links; never navigate away from the editor.
      preview?.addEventListener('click', e => e.preventDefault());

      syncFromNode();
      if (upload) { showProgress(resumed); showState('uploading'); }
      views.setView(initialView({ hasContent: canFinish() }));

      return {
        dom,
        // Typing in the form fields must not reach ProseMirror (Backspace/Enter would act on the node).
        stopEvent: event => Boolean(event.target?.closest?.('input, select, textarea, button, a, [data-resize-handle]')),
        ignoreMutation: () => true,
        update(updatedNode) {
          if (updatedNode.type.name !== 'video') return false;
          const assetChanged = updatedNode.attrs.videoAsset !== currentNode.attrs.videoAsset
            || updatedNode.attrs.videoLink !== currentNode.attrs.videoLink;
          currentNode = updatedNode;
          if (assetChanged) syncFromNode();
          return true;
        },
        destroy() {
          destroyed = true;
          stopPolling();
          unsubscribe?.();
          // The upload carries on in the background when the editor goes away. If the block itself was
          // deleted (not just re-rendered or the editor closed), don't encode a video nobody will see.
          const localId = upload?.localId;
          const { blockId } = currentNode.attrs;
          if (!localId) return;
          setTimeout(() => {
            if (editor.isDestroyed || findBlock(blockId)) return;
            manager.cancel(localId);
          }, 0);
        },
      };
    };
  },

  addCommands() {
    return {
      insertVideo: () => ({ chain }) => chain().insertContent({ type: this.name }).run(),
    };
  },
});

export const videoSlashItem = {
  title: 'Video',
  description: 'Upload a video or paste a link',
  icon: '▶',
  group: 'Blocks',
  keywords: ['video', 'movie', 'clip', 'film', 'mp4', 'youtube', 'vimeo', 'link'],
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).insertVideo().run();
  },
};
